/**
 * <DuplicateGuestPanel> — PR2-2 · Non-destructive duplicate resolution.
 *
 * A reusable panel that:
 *   1. Debounce-checks the candidate guest payload against
 *      /api/pms/guests/check-duplicate.
 *   2. Renders match cards (name, code, phone, email, VIP, prior stays).
 *   3. Exposes 4 decisions:
 *        · open_existing            — opens /pms/guests/{id} in a new tab
 *        · continue_with_existing   — passes an existing guest_id up to parent
 *        · create_new_with_reason   — asks for a reason, then confirms
 *        · ignore                   — dismisses the panel
 *   4. Records every decision via /api/pms/guests/duplicate-decisions
 *      (also updates the matched guests' timeline + audit log).
 *
 * IMPORTANT: this component NEVER merges data. See PR2-2 architectural
 * amendment (deferred to a future Guest Merge module with supervisor
 * approval).
 *
 * Props:
 *   payload:  { first_name, last_name, primary_phone, email,
 *               govt_id_number, date_of_birth }
 *   source:   'guest_form' | 'walk_in' | 'reservation' | 'on_demand'
 *   onDecision(decision, matches, {reason, existingGuestId})
 *     — parent decides how to progress the flow.
 *   autoCheck (default true) — automatically calls check-duplicate on payload
 *     change.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ExternalLink, UserCheck, UserPlus, X, ShieldAlert, Star } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/pages/fo/foApi";

const SOURCE_LABEL = {
  guest_form: "Guest creation form",
  walk_in: "Walk-in",
  reservation: "Reservation",
  on_demand: "Manual search",
};

function _isMeaningful(payload) {
  return Boolean(
    (payload.primary_phone && payload.primary_phone.replace(/\D/g, "").length >= 7) ||
    (payload.email && payload.email.includes("@")) ||
    (payload.govt_id_number && payload.govt_id_number.length >= 4) ||
    (payload.first_name && payload.last_name && payload.date_of_birth)
  );
}

export default function DuplicateGuestPanel({
  payload,
  source = "guest_form",
  onDecision,
  autoCheck = true,
  allowContinueWithExisting = true,
  className = "",
  testid = "dup-panel",
}) {
  const [matches, setMatches] = useState([]);
  const [checking, setChecking] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [reasonPromptFor, setReasonPromptFor] = useState(null); // matches snapshot
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canCheck = useMemo(() => _isMeaningful(payload), [payload]);

  const runCheck = useCallback(async () => {
    if (!canCheck) { setMatches([]); return; }
    setChecking(true);
    try {
      const body = {
        primary_phone: payload.primary_phone || undefined,
        email: payload.email || undefined,
        govt_id_number: payload.govt_id_number || undefined,
        first_name: payload.first_name || undefined,
        last_name: payload.last_name || undefined,
        date_of_birth: payload.date_of_birth || undefined,
      };
      const r = await api.post("/pms/guests/check-duplicate", body);
      setMatches(r.data.matches || []);
      setDismissed(false);
    } catch {
      // Silent fail — duplicate check is advisory
    } finally { setChecking(false); }
  }, [payload, canCheck]);

  useEffect(() => {
    if (!autoCheck) return;
    if (!canCheck) { setMatches([]); return; }
    const t = setTimeout(runCheck, 550);
    return () => clearTimeout(t);
  }, [runCheck, autoCheck, canCheck]);

  const recordDecision = async (decision, extras = {}) => {
    setSubmitting(true);
    try {
      await api.post("/pms/guests/duplicate-decisions", {
        decision,
        source,
        matched_guest_ids: matches.map(m => m.id),
        new_guest_id: extras.newGuestId || null,
        reason: extras.reason || null,
        payload_snapshot: payload,
      });
    } catch (e) {
      // Non-blocking — decision recording failure should not stop workflow
      console.warn("Duplicate decision record failed", e);
    } finally { setSubmitting(false); }
    onDecision?.(decision, matches, extras);
  };

  const onOpenExisting = async (m) => {
    await recordDecision("open_existing", { existingGuestId: m.id });
    window.open(`/pms/guests/${m.id}`, "_blank", "noopener,noreferrer");
  };
  const onContinueWithExisting = async (m) => {
    await recordDecision("continue_with_existing", { existingGuestId: m.id });
    toast.success(`Continuing with existing guest ${m.first_name} ${m.last_name || ""}`);
  };
  const onCreateNewAnyway = () => setReasonPromptFor(matches);
  const onIgnore = async () => {
    await recordDecision("ignore");
    setDismissed(true);
    toast.info("Match ignored — flag dismissed for this flow");
  };

  const confirmCreateNew = async () => {
    if (!reason.trim()) { toast.error("Reason is required to create a new guest"); return; }
    // Parent handles the actual create; it will call `recordDecision('create_new_with_reason', ...)`
    // after the create succeeds so `new_guest_id` can be persisted. We surface the reason via onDecision.
    onDecision?.("create_new_with_reason", matches, { reason: reason.trim() });
    setReasonPromptFor(null);
    setReason("");
  };

  if (!canCheck || dismissed || matches.length === 0) return null;

  return (
    <div className={`rounded-xl border-2 border-amber-300 bg-amber-50 p-4 ${className}`} data-testid={testid}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[14px] font-bold text-amber-900" data-testid={`${testid}-title`}>
              {matches.length} possible duplicate{matches.length > 1 ? "s" : ""} found
            </span>
            {checking && <span className="text-[11px] text-amber-700 animate-pulse">checking…</span>}
          </div>
          <div className="text-[12px] text-amber-800 mb-3">
            Detected during: <b>{SOURCE_LABEL[source] || source}</b>. Review before creating a new guest.
          </div>

          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {matches.map(m => (
              <div key={m.id} className="rounded-lg bg-white border border-amber-200 p-3" data-testid={`${testid}-match-${m.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[14px] font-semibold text-slate-900">
                        {[m.first_name, m.last_name].filter(Boolean).join(" ") || "—"}
                      </span>
                      {m.vip_status && m.vip_status !== "none" && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                          <Star className="w-3 h-3 fill-amber-500" /> {m.vip_status}
                        </span>
                      )}
                      {m.is_blacklisted && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-red-50 px-1.5 py-0.5 rounded">
                          <ShieldAlert className="w-3 h-3" /> blacklisted
                        </span>
                      )}
                      {m.source_collection === "guests_v2" && (
                        <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">walk-in record</span>
                      )}
                    </div>
                    <div className="text-[12px] text-slate-500 mt-0.5">
                      {m.code ? <span className="font-mono">{m.code}</span> : null}
                      {m.primary_phone && <> · {m.primary_phone}</>}
                      {m.email && <> · {m.email}</>}
                      {m.total_stays > 0 && <> · {m.total_stays} prior {m.total_stays === 1 ? "stay" : "stays"}</>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => onOpenExisting(m)}
                      disabled={submitting}
                      className="min-h-[40px] px-3 rounded-lg bg-white border-2 border-slate-300 hover:border-slate-900 text-[12px] font-semibold flex items-center gap-1"
                      data-testid={`${testid}-open-${m.id}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> Open
                    </button>
                    {allowContinueWithExisting && (
                      <button
                        onClick={() => onContinueWithExisting(m)}
                        disabled={submitting}
                        className="min-h-[40px] px-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-[12px] font-semibold flex items-center gap-1"
                        data-testid={`${testid}-continue-${m.id}`}
                      >
                        <UserCheck className="w-3.5 h-3.5" /> Continue with this guest
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap gap-2 justify-end pt-2 border-t border-amber-200">
            <button
              onClick={onIgnore}
              disabled={submitting}
              className="min-h-[40px] px-3 rounded-lg bg-white border-2 border-slate-300 hover:border-slate-900 text-[12px] font-semibold flex items-center gap-1"
              data-testid={`${testid}-ignore`}
            >
              <X className="w-3.5 h-3.5" /> Ignore this match
            </button>
            <button
              onClick={onCreateNewAnyway}
              disabled={submitting}
              className="min-h-[40px] px-3 rounded-lg bg-amber-600 text-white hover:bg-amber-700 text-[12px] font-semibold flex items-center gap-1"
              data-testid={`${testid}-create-new`}
            >
              <UserPlus className="w-3.5 h-3.5" /> Create new guest anyway
            </button>
          </div>
        </div>
      </div>

      {/* Reason prompt */}
      {reasonPromptFor && (
        <div className="mt-4 rounded-lg bg-white border-2 border-amber-400 p-3" data-testid={`${testid}-reason-prompt`}>
          <div className="text-[13px] font-semibold text-slate-900 mb-1">
            Why are you creating a new guest instead of using the existing profile?
          </div>
          <div className="text-[11px] text-slate-500 mb-2">
            This reason is stored on the new guest, the matched guests&apos; timelines, and in audit.
          </div>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Different person with same phone number. Confirmed via passport."
            className="w-full min-h-[80px] border-2 border-slate-300 rounded-lg p-2 text-[13px] focus:border-amber-500 outline-none"
            data-testid={`${testid}-reason-input`}
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => { setReasonPromptFor(null); setReason(""); }}
              className="min-h-[40px] px-3 rounded-lg bg-white border-2 border-slate-300 hover:border-slate-900 text-[12px] font-semibold"
              data-testid={`${testid}-reason-cancel`}
            >
              Cancel
            </button>
            <button
              onClick={confirmCreateNew}
              disabled={!reason.trim()}
              className="min-h-[40px] px-3 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 text-[12px] font-semibold"
              data-testid={`${testid}-reason-confirm`}
            >
              Confirm & Create New
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Standalone helper — used by parent after a "create_new_with_reason" flow
 *  once the new guest has actually been created, to record the decision +
 *  attach possible_duplicate_of + reason to audit.
 */
export async function recordCreateNewDecision({
  source, matches, newGuestId, reason, payloadSnapshot,
}) {
  try {
    await api.post("/pms/guests/duplicate-decisions", {
      decision: "create_new_with_reason",
      source,
      matched_guest_ids: matches.map(m => m.id),
      new_guest_id: newGuestId,
      reason,
      payload_snapshot: payloadSnapshot,
    });
  } catch (e) {
    toast.error(formatApiError(e, "Failed to record duplicate decision"));
  }
}
